<template>
  <div class="area-panel" :class="{ 'area-panel-collapsed': collapsed }">
    <!-- 面板控制栏 -->
    <div class="panel-control">
      <h4 class="panel-title">
        <i class="el-icon-crop"></i>
        {{ isEditing ? (isEditingExisting ? '编辑区域' : '新建区域') : '区域管理' }}
      </h4>
      <div class="panel-actions">
        <!-- 操作按钮 - 仅在未收起时显示 -->
        <template v-if="!collapsed">
          <!-- 保存按钮 - 有绘制内容或正在编辑时显示 -->
          <el-button 
            v-if="(hasDrawingCoordinates && !isEditing) || isEditing"
            type="primary" 
            size="mini"
            icon="el-icon-check" 
            @click="isEditing ? saveArea() : createAreaFromDrawing()" 
            :loading="saving">
            保存
          </el-button>
          
          <!-- 取消按钮 - 有绘制内容或正在编辑时显示 -->
          <el-button 
            v-if="(hasDrawingCoordinates && !isEditing) || isEditing"
            size="mini"
            icon="el-icon-close" 
            @click="isEditing ? cancelEdit() : clearDrawing()">
            取消
          </el-button>
        </template>
        
        <el-tooltip :content="collapsed ? '展开' : '收起'" placement="top">
          <i 
            :class="collapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'" 
            class="collapse-icon"
            @click="toggleCollapse">
          </i>
        </el-tooltip>
      </div>
    </div>

    <!-- 面板内容区域 -->
    <div class="panel-content" v-show="!collapsed">
      <div v-if="loading" class="panel-loading">
        <el-skeleton :rows="3" animated />
      </div>

      <div v-else>
        <div v-if="!isEditing">
          <!-- 区域列表（非编辑模式） -->
            <div class="area-toolbar">
              <el-button
                type="primary"
                size="small"
                icon="el-icon-plus"
                :class="{'is-active': isDrawingActive}"
                @click="toggleDrawing">
                新增区域
              </el-button>
            </div>

          <div class="area-list-container">
              <h5 class="list-title">
                已保存区域
                <span class="area-count">({{ areas.length }})</span>
              </h5>

              <div class="area-list">
                <div 
                  v-for="area in areas" 
                  :key="area.id"
                  class="area-item"
                  :class="{'area-item-selected': selectedAreaId === area.id}"
                  @click="selectArea(area)">
                  <div class="area-info">
                    <span class="area-name">{{ area.name || area.areaName || '未命名区域' }}</span>
                    <small class="area-id">类型: {{ getAreaTypeName(area.areaType) }}</small>
                  </div>
                    <div class="area-actions">
                      <el-button 
                        type="text" 
                        icon="el-icon-view" 
                        size="mini"
                        :title="area.visible === false ? '显示区域' : '隐藏区域'"
                        :class="{ 'is-area-hidden': area.visible === false }"
                        @click.stop="toggleAreaVisibility(area)">
                      </el-button>
                    <el-button 
                      type="text" 
                      icon="el-icon-edit" 
                      size="mini"
                    title="编辑区域"
                      @click.stop="editCurrentArea(area)">
                    </el-button>
                    <el-button 
                      type="text" 
                      icon="el-icon-delete" 
                      size="mini"
                    title="删除区域"
                      @click.stop="confirmDeleteArea(area)">
                    </el-button>
                  </div>
                </div>

                <div v-if="areas.length === 0" class="no-areas">
                  暂无保存的区域，请绘制新区域
              </div>
                </div>
              </div>
            </div>

        <div v-else>
          <!-- 区域编辑模式 -->
          <div class="area-form">
              <!-- 表单标题栏 -->
              <div class="form-header">
                <h5 class="form-title">
                  <i class="el-icon-edit"></i>
                  区域配置
                </h5>
                <el-tooltip :content="formCollapsed ? '展开表单' : '收起表单'" placement="top">
                  <i 
                    :class="formCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" 
                    class="form-collapse-icon"
                    @click="toggleFormCollapse">
                  </i>
                </el-tooltip>
              </div>
              
              <!-- 表单内容区域 -->
              <div class="form-content" v-show="!formCollapsed">
            
              <div class="form-item">
                <span class="label">区域名称：</span>
                <el-input 
                  v-model="localAreaForm.name" 
                  placeholder="请输入区域名称"
                  size="small"
                  @input="handleNameInput">
                </el-input>
              </div>

              <div class="form-item">
                <span class="label">区域类型：</span>
                <el-select 
                  v-model="localAreaForm.areaType" 
                  placeholder="请选择区域类型"
                  size="small"
                  style="width: 100%;">
                  <el-option
                    v-for="option in areaTypeOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value">
                  </el-option>
                </el-select>
              </div>
              
              <!--区域图形颜色：点击按钮打开颜色选择器-->
              <div class="form-item">
                <div style="display: flex; align-items: center;">
                  <span>区域颜色：</span>
                  <el-color-picker 
                    v-model="localAreaForm.color" 
                    @change="handleColorChange">
                  </el-color-picker>
                </div>
              </div>
              <div class="form-item">
                <span class="label">标注类型：</span>
                <el-radio-group 
                  v-model="localAreaForm.geometryType" 
                  size="small"
                  :disabled="isGeometryTypeDisabled">
                  <el-radio-button label="POINT">点</el-radio-button>
                  <el-radio-button label="LINESTRING">线段</el-radio-button>
                  <el-radio-button label="RECTANGLE">矩形</el-radio-button>
                  <el-radio-button label="CIRCLE">圆形</el-radio-button>
                  <el-radio-button label="POLYGON">多边形</el-radio-button>
                </el-radio-group>
                <!-- 编辑模式下的提示信息 -->
                <div v-if="isGeometryTypeDisabled" class="form-help">
                  <i class="el-icon-info"></i>
                  编辑模式下不能修改标注类型，以保持数据一致性
                </div>
              </div>
              
              <!-- 点标注图标选择区域 - 仅在标注类型为POINT时显示 -->
              <div class="form-item" v-if="localAreaForm.geometryType === 'POINT'">
                <span class="label">点标注图标：</span>
                <div class="icon-selector">
                  <div 
                    v-for="icon in pointIcons" 
                    :key="icon"
                    class="icon-item"
                    :class="{'icon-selected': localAreaForm.icon === icon}"
                    @click="selectPointIcon(icon)">
                    <i :class="icon"></i>
                  </div>
                </div>
              </div>
              
              <!-- 点标注经纬度显示区域 - 仅在标注类型为POINT时显示 -->
              <div class="form-item" v-if="localAreaForm.geometryType === 'POINT'">
                <span class="label">点位置：</span>
                <div class="point-coordinates">
                  <div class="coordinate-item">
                    <span class="coordinate-label">经度：</span>
                    <span class="coordinate-value">{{ localAreaForm.pointCoordinates ? formatLongitudeDMS(localAreaForm.pointCoordinates[1]) : '未设置' }}</span>
                  </div>
                  <div class="coordinate-item">
                    <span class="coordinate-label">纬度：</span>
                    <span class="coordinate-value">{{ localAreaForm.pointCoordinates ? formatLatitudeDMS(localAreaForm.pointCoordinates[0]) : '未设置' }}</span>
                  </div>
                </div>
              </div>

              <div class="form-item">
                <span class="label">描述：</span>
                <el-input 
                  v-model="localAreaForm.description" 
                  type="textarea" 
                  rows="2"
                  placeholder="请输入区域描述" 
                  size="small">
                </el-input>
              </div>
              </div>
              <!-- 表单内容区域结束 -->
            
            <!-- 区域警戒规则列表 -->
            <div class="area-alert-rules" v-if="isEditingExisting">
              <div class="alert-rules-header">
                <h5>警戒规则</h5>
                <el-button 
                  type="text" 
                  size="mini"
                  icon="el-icon-plus" 
                  @click="addNewRule">
                  添加规则
                </el-button>
            </div>
              
              <div v-if="loadingRules" class="rules-loading">
                <el-skeleton :rows="2" animated />
    </div>
              
              <div v-else class="alert-rules-list">
                <div 
                  v-for="rule in areaAlertRules" 
                  :key="rule.id" 
                  class="alert-rule-item"
                  @click="openRuleDrawer(rule)">
                  <div class="rule-info">
                    <div class="rule-type">
                      <el-tooltip content="告警级别" placement="top">
                        <el-tag size="mini" :type="getAlertLevelTagType(rule.alertLevel)">
                          {{ getAlertLevelName(rule.alertLevel) }}
                        </el-tag>
                      </el-tooltip>
                      <el-tooltip content="告警动作" placement="top">
                        <el-tag size="mini" :type="getRuleTypeTag(rule.detectionMethod || rule.ruleType || rule.alertType)" style="margin-left: 5px;">
                          {{ getDetectionMethodName(rule.detectionMethod || rule.ruleType || rule.alertType) }}
                        </el-tag>
                      </el-tooltip>
                      <el-tooltip content="规则状态" placement="top">
                        <el-tag size="mini" :type="rule.status === 1 ? 'success' : 'info'" style="margin-left: 5px;">
                          {{ rule.status === 1 ? '启用' : '停用' }}
                        </el-tag>
                      </el-tooltip>
                    </div>
                    <div class="rule-name">{{ rule.ruleName || rule.alertName || rule.name || '未命名规则' }}</div>
                  </div>
                  <div class="rule-actions">
                    <el-tooltip content="编辑规则" placement="top">
                      <el-button 
                        type="text" 
                        icon="el-icon-edit" 
                        size="mini"
                        @click.stop="openRuleDrawer(rule)">
                      </el-button>
                    </el-tooltip>
                    <el-tooltip content="删除规则" placement="top">
                      <el-button 
                        type="text" 
                        icon="el-icon-delete" 
                        size="mini"
                        @click.stop="confirmDeleteRule(rule)">
                      </el-button>
                    </el-tooltip>
                  </div>
                </div>
                
                <div v-if="!areaAlertRules.length" class="no-rules">
                  该区域暂无警戒规则，点击"添加规则"按钮创建
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 规则编辑抽屉 -->
    <bridge-alert-rule-drawer
      v-model="ruleDrawerVisible"
      :rule="currentRule"
      :area-id="localAreaForm.id"
      :bridge-id="bridgeId"
      :area-name="localAreaForm.name || localAreaForm.areaName || '未命名区域'"
      :alert-types="alertTypes"
      :alert-levels="alertLevels"
      :alert-methods="alertMethods"
      :filtered-alert-types="filteredAlertTypes"
      :area-type="localAreaForm.areaType"
      :rule-type="'AREA'"
      @success="handleRuleSaved"
      @close="closeRuleDrawer">
    </bridge-alert-rule-drawer>
  </div>
</template>

<script>
// import AlertRulesList from './AlertRulesList.vue';
import * as alertApi from '@/api/alert';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import BridgeAlertRuleDrawer from './BridgeAlertRuleDrawer.vue'; // 导入新组件
// import mapUtils from '@/utils/mapUtils';
import bridgeMapStore from '@/store/bridgeMapStore';
import { getAreaColor } from '../../utils/mapUtils';

export default {
  name: 'AreaPanel',
  components: {
    // AlertRulesList,
    BridgeAlertRuleDrawer // 注册新组件
  },
  props: {
    // 是否处于加载状态
    loading: {
      type: Boolean,
      default: false
    },
    // 是否正在绘制区域
    isDrawingActive: {
      type: Boolean,
      default: false
    },
    // 区域列表
    areas: {
      type: Array,
      default: () => []
    },
    // 区域类型选项
    areaTypeOptions: {
      type: Array,
      default: () => []
    },
    // 区域类型名称映射
    areaTypeNameMap: {
      type: Object,
      default: () => ({})
    },
    // 绘制的坐标点
    drawingCoordinates: {
      type: Array,
      default: () => []
    },
    // 是否有绘制内容
    hasDrawingCoordinates: {
      type: Boolean,
      default: false
    },
    // 是否正在编辑区域
    isEditing: {
      type: Boolean,
      default: false
    },
    // 是否正在保存
    saving: {
      type: Boolean,
      default: false
    },
    // 区域表单数据
    areaForm: {
      type: Object,
      default: () => ({
        id: null,
        bridgeId: null,
        name: '',
        areaType: '',
        description: '',
        coordinates: ''
      })
    },
    // 当前选中的区域
    selectedArea: {
      type: Object,
      default: null
    },
    // 当前桥梁ID
    bridgeId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      // 是否收起面板
      collapsed: false,
      // 当前选中的区域ID
      selectedAreaId: null,
      // 保存状态标记，防止保存过程中数据被重置
      isSaving: false,
      // 本地区域表单数据
      localAreaForm: {
        id: null,
        bridgeId: null,
        name: '',
        areaType: '',
        geometryType: 'POLYGON', // 默认为多边形
        description: '',
        coordinates: '',
        color: '#FF409E',  // 默认为红色
        icon: 'el-icon-location', // 默认点标注图标
        pointCoordinates: null // 用于存储点标注的经纬度信息
      },
      // 点标注图标列表
      pointIcons: [
        'el-icon-location',
        'el-icon-position',
        'el-icon-place',
        'el-icon-map-location',
        'el-icon-video-camera',
        'el-icon-bell',
        'el-icon-warning',
        'el-icon-info',
        'el-icon-question',
        'el-icon-star-off',
        'el-icon-s-flag',
        'el-icon-data-analysis'
      ],
      // 用户是否编辑过区域名称
      hasUserEditedName: false,
      // 区域警戒规则列表
      areaAlertRules: [],
      // 规则抽屉是否可见
      ruleDrawerVisible: false,
      // 是否加载规则列表中
      loadingRules: false,
      // 当前编辑的规则
      currentRule: null,
      // 规则类型选项
      ruleTypeOptions: [],
      // 检测方法选项
      detectionMethodOptions: [],
      // 告警动作选项
      alertTypes: [],
      // 告警级别选项
      alertLevels: [],
      // 告警方式选项
      alertMethods: [],
      // 选项是否已初始化
      optionsInitialized: false,
      // 防止重复加载的标记
      lastLoadedAreaId: null,
      lastLoadTime: 0,
      loadDebounceTime: 500, // 防抖时间，单位毫秒
      // 编辑按钮禁用状态
      isEditButtonDisabled: false,
      // 编辑模式：edit（编辑现有区域）或 new（新建区域）
      editMode: 'edit',
      // 表单区域收起状态
      formCollapsed: false
    };
  },
  computed: {
    // 是否正在编辑现有区域
    isEditingExisting() {
      // 【修复类型错误】：确保返回布尔值，而不是字符串ID
      return !!(this.isEditing && this.localAreaForm && this.localAreaForm.id);
    },
    
    // 是否禁用标注类型选择器
    isGeometryTypeDisabled() {
      // 编辑现有区域时禁用标注类型选择，防止几何类型不匹配
      return this.isEditingExisting;
    },
    // 不进行过滤
    filteredAlertTypes() {
      return this.alertTypes;
    },
    // 过滤后的告警动作选项
    filteredAlertTypes_V1() {
      if (!this.localAreaForm) return this.alertTypes;
      
      // 区域类型和名称
      const areaType = this.localAreaForm.areaType || '';
      const areaName = this.localAreaForm.name || '';
      
      // 区域类型判断
      if (areaType === 'BRIDGE_LINE' || areaName.includes('桥梁线')) {
        return this.alertTypes.filter(type => ['HIGH'].includes(type.value));
      } else if (areaType === 'LINE' || areaName.includes('线')) {
        return this.alertTypes.filter(type => ['CROSS'].includes(type.value));
      } else if (areaType === 'NAVIGATION' || areaName.includes('通航区')) {
        return this.alertTypes.filter(type => ['YAW', 'STAY', 'ENTRY', 'EXIT'].includes(type.value));
      } else if (areaType === 'PIER' || areaName.includes('桥墩')) {
        return this.alertTypes.filter(type => ['COLLISION'].includes(type.value));
      } else if (areaType === 'ALERT' || areaName.includes('警戒区')) {
        return this.alertTypes.filter(type => ['ENTRY', 'EXIT', 'STAY', 'SPEED'].includes(type.value));
      } else {
        // 普通区域
        return this.alertTypes.filter(type => ['ENTRY', 'EXIT', 'STAY', 'SPEED'].includes(type.value));
      }
    },
    // 判断是否正在编辑区域
    isEditingArea() {
      return this.isEditing && this.isEditingExisting;
    }
  },
  async created() {
    // 监听区域选择事件
    BridgeEventBus.$on(BRIDGE_EVENTS.AREA_SELECTED, this.handleAreaSelectedEvent);
    
    // 监听地图点击事件，用于点标注
    BridgeEventBus.$on(BRIDGE_EVENTS.MAP_CLICK, this.handleMapPointClick);
    
    try {
      // 并行加载所有选项数据
      await Promise.all([
        this.fetchRuleTypeOptions(),
        this.fetchDetectionMethodOptions(),
        this.fetchAlertLevelOptions(),
        this.fetchAlertMethodOptions()
      ]);
      
      // 标记选项已初始化
      this.optionsInitialized = true;
      
      // 触发选项初始化完成事件
      this.$emit('options-initialized');
      
      // 如果当前有选中的区域，加载其警戒规则
      if (this.selectedAreaId && this.isEditingExisting) {
        this.loadAreaAlertRules(this.selectedAreaId);
      }
    } catch (error) {
      console.error('加载选项数据出错:', error);
    }
  },
  beforeDestroy() {
    // 移除事件监听
    BridgeEventBus.$off(BRIDGE_EVENTS.AREA_SELECTED, this.handleAreaSelectedEvent);
    
    // 移除地图点击事件监听
    BridgeEventBus.$off(BRIDGE_EVENTS.MAP_CLICK, this.handleMapPointClick);
  },
  methods: {
    // 切换面板收起状态
    toggleCollapse() {
      this.collapsed = !this.collapsed;
      this.$emit('collapse-change', this.collapsed);
      
      // 给地图组件留出时间调整布局
      setTimeout(() => {
        // 通知地图组件更新大小
        this.$emit('layout-change');
      }, 300);
    },
    
    // 切换表单区域收起状态
    toggleFormCollapse() {
      this.formCollapsed = !this.formCollapsed;
    },
    
    // 获取区域类型名称
    getAreaTypeName(areaType) {
      return this.areaTypeNameMap[areaType] || areaType || '未知类型';
    },
    
    // 编辑当前区域
    editCurrentArea(area) {
      console.log('AreaPanel.editCurrentArea 被调用，区域:', area);
      
      // 检查区域参数
      if (!area) {
        this.$message.warning('请先选择一个区域');
        return;
      }
      
      // 【重要修改】先开始编辑，再处理选择状态
      const success = bridgeMapStore.actions.startEditingArea(area);
      if (!success) {
          // 提供强制解锁选项
          this.$confirm('无法开始编辑该区域，可能正在编辑其他区域。是否强制清理编辑状态？', '编辑冲突', {
            confirmButtonText: '强制解锁',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            // 强制清理编辑锁定状态
            bridgeMapStore.actions.forceUnlockEditing();
            // 重新尝试开始编辑
            const retrySuccess = bridgeMapStore.actions.startEditingArea(area);
            if (retrySuccess) {
              this.$message.success('已清理编辑状态，开始编辑区域');
              // 编辑成功启动后，同步本地选择状态
              this.selectedAreaId = area.id;
              this.isEditButtonDisabled = true;
              this.loadAreaAlertRules(area.id);
              this.$emit('edit-current-area', area);
            } else {
              this.$message.error('强制解锁后仍无法编辑，请检查区域数据');
            }
          }).catch(() => {
            this.$message.info('已取消编辑操作');
          });
          return;
      }
      
      // 编辑成功启动，同步本地选择状态（不调用selectArea避免冲突）
      this.selectedAreaId = area.id;
      this.isEditButtonDisabled = true;
      
      // 加载区域警戒规则
      this.loadAreaAlertRules(area.id);
      
      // 触发编辑当前区域事件，传递区域对象
      this.$emit('edit-current-area', area);
    },
    
    // 处理区域名称输入
    handleNameInput(value) {
      // 如果名称已改变且不为空，标记用户已编辑区域名称
      if (value && value.trim() !== '') {
        const currentTypeName = this.areaTypeNameMap[this.localAreaForm.areaType];
        
        // 仅当输入的名称与当前区域类型名称不同时，才标记为用户编辑
        if (value !== currentTypeName) {
          this.hasUserEditedName = true;
        }
        
        // 确保areaName和name同步
        this.localAreaForm.areaName = value;
        
        // 如果是点标注且存在点标注，同步更新Label
        if (this.localAreaForm.geometryType === 'POINT') {
          this.updatePointMarkerLabel(value);
        }
        
        // 触发区域名称变更事件，通知地图组件更新显示
        this.$emit('area-name-changed', {
          name: value,
          areaId: this.localAreaForm.id,
          geometryType: this.localAreaForm.geometryType
        });
      }
    },
    
    // 更新点标注的Label
    updatePointMarkerLabel(newLabel) {
      try {
        // 查找桥梁地图组件引用
        const bridgeMapComponent = this.findBridgeMapComponent();
        
        if (bridgeMapComponent) {
          // 首先尝试使用新的专用方法
          if (typeof bridgeMapComponent.updatePointMarkerLabel === 'function') {
            bridgeMapComponent.updatePointMarkerLabel(newLabel);
          }
          // 备用方案：直接操作pointMarkers
          else if (bridgeMapComponent.pointMarkers && 
            bridgeMapComponent.pointMarkers.length > 0) {
          // 更新第一个点标注的Label
          const pointMarker = bridgeMapComponent.pointMarkers[0];
          pointMarker.label = newLabel;
          
          console.log('点标注Label已更新为:', newLabel);
          
          // 强制更新视图
          bridgeMapComponent.$forceUpdate();
          }
        }
      } catch (error) {
        console.error('更新点标注Label失败:', error);
      }
    },
    
    // 处理颜色变更 - 主要用于用户直接点击颜色选择器
    handleColorChange(color) {
      console.log('用户选择颜色:', color);
      
      // 更新本地表单数据中的颜色 - 这会触发watcher，后续逻辑由watcher处理
      this.localAreaForm.color = color;
      
      // 如果是点标注且正在绘制，立即更新正在绘制的点标注颜色
      if (this.localAreaForm.geometryType === 'POINT') {
        const bridgeMapComponent = this.findBridgeMapComponent();
        
        // 只更新正在绘制的点标注颜色，已保存的由watcher处理
        if (bridgeMapComponent && 
            bridgeMapComponent.pointMarkers && 
            bridgeMapComponent.pointMarkers.length > 0) {
          const pointMarker = bridgeMapComponent.pointMarkers[0];
          pointMarker.color = color;
          console.log('正在绘制的点标注颜色已实时更新:', color);
          
          // 强制更新视图
          bridgeMapComponent.$forceUpdate();
        }
      }
    },
    
    // 触发绘制模式切换
    toggleDrawing() {
      if (!this.isDrawingActive) {
        // 设置为新建模式
        this.editMode = 'new';
        
        // 自动创建一个新的区域并进入编辑模式
        // 初始化区域表单数据
        const color  =  this.localAreaForm.color || getAreaColor(this.localAreaForm.areaType);
        this.localAreaForm = {
          id: null,
          bridgeId: this.bridgeId,
          name: this.areaTypeNameMap[this.localAreaForm.areaType] || '新区域',
          areaName: this.areaTypeNameMap[this.localAreaForm.areaType] || '新区域',
          areaCode: '',
          areaType: this.localAreaForm.areaType,
          geometryType: this.localAreaForm.geometryType || 'POLYGON',
          description: '',
          coordinates: '',
          color: color,
          isEnabled: 1,
          icon: this.localAreaForm.icon || 'el-icon-location', // 保留当前选中的图标
          pointCoordinates: null // 重置点标注坐标
        };
        
        // 通知父组件进入编辑模式
        this.$emit('edit-area', {isNew: true});
        
        // 传递当前标注类型和点标注的图标 - 强制设置为激活状态
        this.$emit('toggle-drawing', {
          active: true, // 总是设置为true而不是取反
          markType: this.getMarkTypeFromGeometryType(this.localAreaForm.geometryType || 'POLYGON'),
          iconName: this.localAreaForm.geometryType === 'POINT' ? this.localAreaForm.icon : null
        });
      } else {
        // 如果已经处于绘制状态，则不要关闭绘制模式
        // 而是根据当前选择的几何类型更新标注类型
        this.$emit('toggle-drawing', {
          active: true, // 保持绘制模式激活
          markType: this.getMarkTypeFromGeometryType(this.localAreaForm.geometryType || 'POLYGON'),
          iconName: this.localAreaForm.geometryType === 'POINT' ? this.localAreaForm.icon : null
        });
      }
    },
    
    // 将geometryType转换为markType (用于向后兼容)
    getMarkTypeFromGeometryType(geometryType) {
      const mapping = {
        'POINT': 'point',
        'LINESTRING': 'line',
        'RECTANGLE': 'rectangle',
        'CIRCLE': 'circle',
        'POLYGON': 'polygon'
      };
      return mapping[geometryType] || 'polygon';
    },
    
    // 更新点标注坐标
    updatePointCoordinates(coordinates) {
      if (this.localAreaForm.geometryType === 'POINT' && coordinates) {
        // 确保数据格式一致 - 如果是单个点坐标数组，使用它
        // 如果是二维数组且只有一个元素，取第一个元素
        let pointCoords;
        if (Array.isArray(coordinates) && coordinates.length === 2 && !Array.isArray(coordinates[0])) {
          // 如果是形如 [lat, lng] 的简单数组
          pointCoords = coordinates;
        } else if (Array.isArray(coordinates) && coordinates.length >= 1 && Array.isArray(coordinates[0])) {
          // 如果是形如 [[lat, lng]] 的二维数组
          pointCoords = coordinates[0];
        } else {
          console.warn('无效的点标注坐标格式', coordinates);
          return;
        }
        
        // 更新表单中的点坐标
        this.localAreaForm.pointCoordinates = pointCoords;
        
        // 记录更新
        console.log('更新点标注坐标:', this.localAreaForm.pointCoordinates);
      }
    },
    
    // 清除绘制内容
    clearDrawing() {
      // 【修复循环调用】：清理自己的状态，不发出事件避免循环调用
      console.log('AreaPanel: 开始清除绘制内容');
      
      // 重置表单中与绘制相关的状态
      if (this.localAreaForm.pointCoordinates) {
        console.log('清除点标注坐标');
        this.localAreaForm.pointCoordinates = null;
      }
      
      // 【修复props修改错误】：通过事件通知父组件，而不是直接修改props
      // this.isDrawingActive = false; // ❌ 不能直接修改props
      // this.isEditingArea = false;   // ❌ 不能直接修改props
      
      // 通知父组件状态变更
      this.$emit('update:isDrawingActive', false);
      this.$emit('update:isEditingArea', false);
      
      console.log('AreaPanel: 绘制内容清除完成');
    },
    
    // 选择区域
    selectArea(area) {
      console.log('=== AreaPanel: 选择区域 ===');
      console.log('选择的区域:', area);
      console.log('当前selectedAreaId:', this.selectedAreaId);
      
      if (this.selectedAreaId === area.id) {
        // 如果是再次点击相同区域，不执行任何操作
        console.log('重复选择同一区域，跳过');
        return;
      }
      
      // 【重要修改】使用store统一管理区域选择
      console.log('调用store.setSelectedArea前的store状态:', {
        isEditingLocked: bridgeMapStore.getters.isEditingLocked(),
        editingAreaId: bridgeMapStore.getters.editingAreaId()
      });
      
      const success = bridgeMapStore.actions.setSelectedArea(area);
      console.log('setSelectedArea结果:', success);
      
      if (!success) {
        // 如果因为编辑锁定而无法切换，显示提示
        console.log('区域选择失败，编辑锁定中');
        this.$message.warning('当前正在编辑其他区域，请先保存或取消当前编辑');
        return;
      }
      
      // 同步本地状态
      this.selectedAreaId = area.id;
      
      // 如果选中区域更改了，重置编辑按钮状态
      this.isEditButtonDisabled = false;
      this.editMode = 'edit';
      
      // 触发选择区域事件
      this.$emit('select-area', area);
      
      // 加载区域警戒规则
      if (area.id) {
        this.loadAreaAlertRules(area.id);
      }
      
      console.log('AreaPanel区域选择完成，当前selectedAreaId:', this.selectedAreaId);
      console.log('=========================');
    },
    
    // 取消编辑
    cancelEdit() {
      // 【重要修改】使用store统一管理取消编辑
      bridgeMapStore.actions.cancelEditingArea();
      
      // 恢复编辑按钮状态
      this.isEditButtonDisabled = false;
      
      // 不重置区域颜色，保持原有值
      
      // 触发取消编辑事件
      this.$emit('cancel-edit');
    },
    
    // 保存区域
    saveArea() {
      console.log('开始保存区域...');
      
      // 区域名称重名校验（问题#7：同一桥梁下不允许重名区域）
      const areaName = (this.localAreaForm.name || this.localAreaForm.areaName || '').trim();
      if (!areaName) {
        this.$message.warning('请先填写区域名称');
        return;
      }
      const editingId = this.localAreaForm.id;
      const duplicateArea = (this.areas || []).find(a =>
        a.id !== editingId &&
        String(a.areaName || a.name || '').trim() === areaName
      );
      if (duplicateArea) {
        this.$message.warning(`同一桥梁下已存在同名区域「${areaName}」，请更换名称`);
        return;
      }
      
      // 设置保存状态，防止保存过程中数据被重置
      this.isSaving = true;
      
      // 打印保存前的详细状态
      console.log('保存前localAreaForm状态:', {
        id: this.localAreaForm.id,
        geometryType: this.localAreaForm.geometryType,
        icon: this.localAreaForm.icon,
        name: this.localAreaForm.name,
        color: this.localAreaForm.color
      });
      
      // 创建保存数据的副本
      const saveData = { ...this.localAreaForm };
      
      // 对于点标注，确保icon字段正确设置
      if (saveData.geometryType === 'POINT') {
        // 只有在icon字段真正为空时才设置默认值
        if (!saveData.icon || saveData.icon === '' || saveData.icon === null || saveData.icon === undefined) {
          console.warn('点标注icon字段为空，设置默认值。当前值:', saveData.icon);
          saveData.icon = 'el-icon-location';
        } else {
          console.log('保留用户选择的图标:', saveData.icon);
        }
        
        console.log('AreaPanel保存点标注 - icon:', saveData.icon);
      }
      
      // 确保areaName和name字段同步
      if (saveData.name && !saveData.areaName) {
        saveData.areaName = saveData.name;
      } else if (saveData.areaName && !saveData.name) {
        saveData.name = saveData.areaName;
      }
      
      console.log('AreaPanel保存数据:', {
        geometryType: saveData.geometryType,
        icon: saveData.icon,
        name: saveData.name,
        areaName: saveData.areaName
      });
      
      // 同步修改后的表单数据到父组件
      this.$emit('save-area', saveData);
      
      // 延迟清除保存状态（保险措施）
      setTimeout(() => {
        if (this.isSaving) {
          console.log('定时清除保存状态');
          this.isSaving = false;
        }
      }, 3000);
      
      // 此处不立即恢复编辑按钮状态，等待保存成功后再处理
    },
    
    // 保存成功后处理
    handleSaveSuccess() {
      console.log('=== AreaPanel: 保存成功回调 ===');
      console.log('AreaPanel保存前状态:', {
        selectedAreaId: this.selectedAreaId,
        localAreaFormId: this.localAreaForm?.id,
        isSaving: this.isSaving,
        isEditButtonDisabled: this.isEditButtonDisabled,
        isEditingExisting: this.isEditingExisting
      });
      
      // 【修复】不在这里调用endEditingArea，由BridgeManagement统一管理
      // bridgeMapStore.actions.endEditingArea(); // 已在BridgeManagement中调用
      
      // 清除保存状态标记
      this.isSaving = false;
      
      // 恢复编辑按钮状态
      this.isEditButtonDisabled = false;
      
      // 【关键修复】清除区域选择和表单状态，退出编辑模式
      this.selectedAreaId = null;
      this.localAreaForm = {
        id: null,
        bridgeId: null,
        name: '',
        areaName: '',
        areaCode: '',
        areaType: '',
        geometryType: 'POLYGON',
        description: '',
        coordinates: '',
        color: '#FF409E',
        isEnabled: 1,
        icon: 'el-icon-location',
        pointCoordinates: null
      };
      
      // 重置编辑相关状态
      this.hasUserEditedName = false;
      this.editMode = 'edit';
      
      console.log('AreaPanel保存后状态:', {
        selectedAreaId: this.selectedAreaId,
        localAreaFormId: this.localAreaForm?.id,
        isSaving: this.isSaving,
        isEditButtonDisabled: this.isEditButtonDisabled,
        isEditingExisting: this.isEditingExisting
      });
      console.log('AreaPanel: 所有状态已重置，退出编辑模式');
      console.log('===========================');
    },
    
    // 创建区域从绘制内容
    createAreaFromDrawing() {
      // 设置为新建模式
      this.editMode = 'new';
      
      // 触发创建区域事件
      this.$emit('create-area-from-drawing');
    },
    
    // 加载规则类型选项
    async fetchRuleTypeOptions() {
      try {
        const res = await alertApi.getRuleTypeOptions();
        if (res.code === 200 && Array.isArray(res.data)) {
          this.ruleTypeOptions = res.data.map(item => ({
            label: item.label || item.name,
            value: item.value || item.code
          }));
        } else {
          console.error('获取规则类型选项失败:', res.msg || '未知错误');
          this.ruleTypeOptions = [];
        }
      } catch (error) {
        console.error('获取规则类型选项出错:', error);
        this.ruleTypeOptions = [];
      }
    },
    
    // 加载检测方法选项
    async fetchDetectionMethodOptions() {
      try {
        const res = await alertApi.getRuleTypeOptions();
        if (res.code === 200 && Array.isArray(res.data)) {
          this.detectionMethodOptions = res.data.map(item => ({
            label: item.label || item.name,
            value: item.value || item.code
          }));
          this.alertTypes = [...this.detectionMethodOptions]; // 确保两个数组都有相同的数据
      } else {
          console.error('获取检测方法选项失败:', res.msg || '未知错误');
          this.detectionMethodOptions = [];
          this.alertTypes = [];
        }
      } catch (error) {
        console.error('获取检测方法选项出错:', error);
        this.detectionMethodOptions = [];
        this.alertTypes = [];
      }
    },
    
    // 加载告警级别选项
    async fetchAlertLevelOptions() {
      try {
        const res = await alertApi.getAlertLevelOptions();
        if (res.code === 200 && Array.isArray(res.data)) {
          this.alertLevels = res.data.map(item => ({
            label: item.label || item.name,
            value: item.value || item.code
          }));
        } else {
          console.error('获取告警级别选项失败:', res.msg || '未知错误');
          this.alertLevels = [];
        }
      } catch (error) {
        console.error('获取告警级别选项出错:', error);
        this.alertLevels = [];
      }
    },

    // 加载告警方式选项
    async fetchAlertMethodOptions() {
      try {
        const res = await alertApi.getAlertMethodOptions();
        if (res.code === 200 && Array.isArray(res.data)) {
          this.alertMethods = res.data.map(item => ({
            label: item.label || item.name,
            value: item.value || item.code
          }));
        } else {
          console.error('获取告警方式选项失败:', res.msg || '未知错误');
          this.alertMethods = [];
        }
      } catch (error) {
        console.error('获取告警方式选项出错:', error);
        this.alertMethods = [];
      }
    },
    
    // 加载区域警戒规则
    async loadAreaAlertRules(areaId) {
      if (!areaId || !this.bridgeId) {
        console.warn('无法加载警戒规则：缺少必要参数');
        this.areaAlertRules = [];
        return;
      }
      
      // 防重复加载机制：如果是同一区域且距离上次加载时间不足防抖时间，则跳过
      const now = Date.now();
      if (areaId === this.lastLoadedAreaId && (now - this.lastLoadTime) < this.loadDebounceTime) {
        console.log(`跳过重复加载区域(${areaId})的警戒规则，间隔时间: ${now - this.lastLoadTime}ms`);
        return;
      }
      
      // 更新加载记录
      this.lastLoadedAreaId = areaId;
      this.lastLoadTime = now;
      
      // 如果选项未初始化完成，等待选项加载
      if (!this.optionsInitialized) {
        setTimeout(() => {
          if (this.optionsInitialized) {
            this.loadAreaAlertRules(areaId);
          }
        }, 500);
        return;
      }
      
      try {
        this.loadingRules = true;
        
        const params = {
          areaId: areaId,
          bridgeId: this.bridgeId,
          pageNum: 1,
          pageSize: 50
        };
        
        // 使用getAlertRules API
        const res = await alertApi.getAlertRules(params);
        
        if (res && res.code === 200) {
          // 处理规则数据，确保关键字段有值
          const rules = (res.data.list || []).map(rule => {
            const processedRule = { ...rule };
            
            // 确保detectionMethod字段有值
            if (!processedRule.detectionMethod) {
              if (processedRule.ruleType) {
                processedRule.detectionMethod = processedRule.ruleType;
              } else if (processedRule.alertType) {
                processedRule.detectionMethod = processedRule.alertType;
              }
            }
            
            // 确保ruleName字段有值（API使用的主要字段）
            if (!processedRule.ruleName) {
              if (processedRule.alertName) {
                processedRule.ruleName = processedRule.alertName;
              } else if (processedRule.name) {
                processedRule.ruleName = processedRule.name;
              }
            }
            
            // 确保alertName字段有值（兼容前端代码）
            if (!processedRule.alertName && processedRule.ruleName) {
              processedRule.alertName = processedRule.ruleName;
            }
            
            // 处理告警方式字段
            if (typeof processedRule.alertMethods === 'string' && processedRule.alertMethods) {
              processedRule.alertMethods = processedRule.alertMethods.split(',');
            } else if (!Array.isArray(processedRule.alertMethods)) {
              processedRule.alertMethods = [];
            }
            
            // 确保status字段为数字
            if (processedRule.status === true) {
              processedRule.status = 1;
            } else if (processedRule.status === false) {
              processedRule.status = 0;
            }
            
            return processedRule;
          });
          
          this.areaAlertRules = rules;
        } else {
          this.$message.error(res && res.msg ? res.msg : '获取区域警戒规则失败');
          this.areaAlertRules = [];
        }
      } catch (error) {
        console.error('获取区域警戒规则出错:', error);
        this.$message.error('获取区域警戒规则失败');
        this.areaAlertRules = [];
      } finally {
        this.loadingRules = false;
      }
    },
    
    // 打开规则抽屉 - 查看/编辑现有规则
    openRuleDrawer(rule) {
      this.currentRule = rule; // 设置当前规则对象
      this.ruleDrawerVisible = true; // 显示抽屉
    },
    
    // 关闭规则抽屉
    closeRuleDrawer() {
      this.ruleDrawerVisible = false;
      this.currentRule = null;
    },
    
    // 添加新规则
    addNewRule() {
      // 打开抽屉并传递null作为参数，表示创建新规则
      this.currentRule = null;
      this.ruleDrawerVisible = true;
    },
    
    // 确认删除规则
    confirmDeleteRule(rule) {
      this.$confirm('确定要删除该警戒规则吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await alertApi.deleteAlertRule(rule.id);
          if (res && res.code === 200) {
            this.$message.success('删除规则成功');
            
            // 重新加载规则列表
            this.loadAreaAlertRules(this.localAreaForm.id);
          } else {
            this.$message.error((res && res.msg) || '删除规则失败');
          }
        } catch (error) {
          console.error('删除规则出错:', error);
          this.$message.error('删除规则失败');
        }
      }).catch(() => {
        // 取消删除操作
      });
    },
    
    // 获取规则类型标签样式
    getRuleTypeTag(ruleType) {
      if (!ruleType) return 'info';
      
      // 尝试从规则类型选项中获取标签类型
      const found = this.ruleTypeOptions.find(opt => opt.value === ruleType);
      if (found && found.tagType) {
        return found.tagType;
      }
      
      
      return 'info';
    },
    
    // 获取规则类型名称
    getRuleTypeName(ruleType) {
      const found = this.ruleTypeOptions.find(opt => opt.value === ruleType);
      return found ? found.label : ruleType;
    },

    // 获取检测方法名称
    getDetectionMethodName(detectionMethod) {
      if (!detectionMethod) return '未知';
      
      // 首先从告警类型选项中查找
      const found = this.alertTypes.find(opt => opt.value === detectionMethod);
      if (found) return found.label;
      
      // 如果在告警类型中找不到，尝试在规则类型选项中查找
      const foundInRuleTypes = this.ruleTypeOptions.find(opt => opt.value === detectionMethod);
      if (foundInRuleTypes) return foundInRuleTypes.label;
      
      // 如果在所有选项中都找不到，返回原值
      return detectionMethod;
    },

    // 获取告警级别标签类型
    getAlertLevelTagType(alertLevel) {
      if (!alertLevel) return 'info';
      
      // 尝试从告警级别选项中获取标签类型
      const found = this.alertLevels.find(opt => opt.value === alertLevel);
      if (found && found.tagType) {
        return found.tagType;
      }
      
      return 'info';
    },

    // 获取告警级别名称
    getAlertLevelName(alertLevel) {
      if (!alertLevel) return '未知';
      
      // 从API获取的options中查找
      const found = this.alertLevels.find(opt => opt.value === alertLevel);
      if (found) return found.label;
      
      // 如果在options中找不到，返回原值
      return alertLevel;
    },

    // 处理规则保存成功后的回调
    handleRuleSaved() {
      this.loadAreaAlertRules(this.localAreaForm.id);
    },

    // 处理区域选择事件
    handleAreaSelectedEvent(area /* , skipEdit */) {
      // 为了兼容性，检查是否是数组形式的参数
      if (Array.isArray(area) && area.length >= 1) {
        // 如果是数组，则第一个元素是area，第二个是skipEdit
        // skipEdit = area.length > 1 ? area[1] : skipEdit;
        area = area[0];
      }
      
      // 防止空引用
      if (!area) {
        console.warn('handleAreaSelectedEvent: 收到无效区域对象');
        return;
      }
      
      if (area && area.id) {
        this.selectedAreaId = area.id;
        
        // 确保选项已初始化完成
        if (this.optionsInitialized) {
          // 加载该区域的警戒规则
          this.loadAreaAlertRules(area.id);
        } else {
          // 设置一个标记，等选项加载完成后再加载规则
          this.$once('options-initialized', () => {
            this.loadAreaAlertRules(area.id);
          });
        }
      }
    },
    
    // 切换区域可见性
    toggleAreaVisibility(area) {
      // 复制区域对象，避免直接修改props
      const updatedArea = { ...area };
      
      // 切换区域可见性状态
      updatedArea.visible = updatedArea.visible === false ? true : false;
      
      // 发射事件通知父组件更新区域可见性
      this.$emit('toggle-area-visibility', updatedArea);
      
      // 使用事件总线通知地图组件更新区域可见性
      BridgeEventBus.$emit(BRIDGE_EVENTS.AREA_VISIBILITY_CHANGED, updatedArea);
    },
    
    // 确认删除区域
    confirmDeleteArea(area) {
      this.$confirm('确定要删除该区域吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$emit('delete-area', area);
      }).catch(() => {
        // 取消删除操作
      });
    },
    
    // 取消编辑区域
    cancelEditArea() {
      // 恢复编辑按钮状态
      this.isEditButtonDisabled = false;
      
      // 检查是否有未保存的绘制内容
      const hasUnsavedChanges = this.drawingCoordinates.length > 2 && 
        this.isEditingArea && 
        (!this.localAreaForm.coordinates || 
          (typeof this.localAreaForm.coordinates === 'string' && 
          this.drawingCoordinates.length !== JSON.parse(this.localAreaForm.coordinates).length));
      
      if (hasUnsavedChanges) {
        this.$confirm('有未保存的区域绘制内容，确定要取消吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '继续编辑',
          type: 'warning'
        }).then(() => {
          this.resetAreaForm();
        }).catch(() => {
          // 用户选择继续编辑，不执行任何操作
        });
      } else {
        // 没有实质内容或没有变化，直接取消
        this.resetAreaForm();
      }
    },
    
    // 重置区域表单
    resetAreaForm() {
      // 不重置区域颜色，保持原有值
      
      // 触发重置事件
      this.$emit('cancel-edit');
      this.areaAlertRules = [];
    },

    // 清除当前区域绘制内容
    clearCurrentArea() {
      this.$emit('clear-current-area');
    },

    // 选择点标注图标
    selectPointIcon(iconName) {
      // 更新当前表单数据中的图标名称
      this.localAreaForm.icon = iconName;
      
      console.log('选择点标注图标:', iconName, '当前几何类型:', this.localAreaForm.geometryType);
      
      // 查找桥梁地图组件引用
      const bridgeMapComponent = this.findBridgeMapComponent();
      
      if (bridgeMapComponent && this.localAreaForm.geometryType === 'POINT') {
        // 1. 更新正在绘制的点标注图标（如果存在）
        if (bridgeMapComponent.pointMarkers && bridgeMapComponent.pointMarkers.length > 0) {
          const pointMarker = bridgeMapComponent.pointMarkers[0];
          pointMarker.iconName = iconName;
          console.log('已更新正在绘制的点标注图标:', iconName);
          
          // 强制更新视图
          bridgeMapComponent.$forceUpdate();
        }
        
        // 2. 使用BridgeMap提供的专用方法更新图标
        else if (typeof bridgeMapComponent.updatePointMarkerIcon === 'function') {
          const result = bridgeMapComponent.updatePointMarkerIcon(iconName);
          console.log('调用updatePointMarkerIcon结果:', result);
        }
        
        // 3. 如果是已保存的点标注编辑，更新已保存的数据
        if (this.isEditingExisting && this.localAreaForm.id) {
          // 直接在savedAreas中查找并更新对应的点标注
          if (bridgeMapComponent.savedAreas) {
            const area = bridgeMapComponent.savedAreas.find(a => a.id === this.localAreaForm.id);
            if (area && area.geometryType === 'POINT') {
              area.icon = iconName;
              area.iconName = iconName;
              console.log('已更新savedAreas中的点标注图标:', iconName);
              
              // 强制更新计算属性
              bridgeMapComponent.mapUpdateCounter++;
              bridgeMapComponent.$forceUpdate();
            }
          }
          
          // 触发图标更新事件
          this.$emit('point-icon-changed', {
            iconName: iconName,
            areaId: this.localAreaForm.id,
            areaType: this.localAreaForm.areaType
          });
        }
        
        // 4. 同时更新bridgeMapStore中的当前区域（如果存在）
        const currentArea = bridgeMapComponent.$store ? bridgeMapComponent.$store.state.currentArea : null;
        if (currentArea && currentArea.geometryType === 'POINT') {
          currentArea.icon = iconName;
          currentArea.iconName = iconName;
          console.log('已更新store中当前区域的图标:', iconName);
        }
      }
      
      // 触发图标选择事件
      this.$emit('select-point-icon', iconName);
      
      console.log('点标注图标选择完成:', iconName, '区域ID:', this.localAreaForm.id);
    },
    
    // 查找桥梁地图组件引用
    findBridgeMapComponent() {
      // 尝试从父组件找到BridgeMap组件引用
      if (this.$parent && this.$parent.$refs && this.$parent.$refs.bridgeMap) {
        return this.$parent.$refs.bridgeMap;
      }
      
      // 如果找不到，返回null
      return null;
    },

    // 格式化经度为 DMS 格式
    formatLongitudeDMS(longitude) {
      if (longitude === null || longitude === undefined) return 'N/A';
      const absLon = Math.abs(longitude);
      const degrees = Math.floor(absLon);
      const minutes = Math.floor((absLon - degrees) * 60);
      const seconds = Math.floor(((absLon - degrees) * 60 - minutes) * 60);
      const direction = longitude < 0 ? 'W' : 'E';
      return `${degrees}°${minutes}'${seconds}"${direction}`;
    },

    // 格式化纬度为 DMS 格式
    formatLatitudeDMS(latitude) {
      if (latitude === null || latitude === undefined) return 'N/A';
      const absLat = Math.abs(latitude);
      const degrees = Math.floor(absLat);
      const minutes = Math.floor((absLat - degrees) * 60);
      const seconds = Math.floor(((absLat - degrees) * 60 - minutes) * 60);
      const direction = latitude < 0 ? 'S' : 'N';
      return `${degrees}°${minutes}'${seconds}"${direction}`;
    },

    // 处理地图点击事件 - 用于点标注
    handleMapPointClick(latlng) {
      // 仅在绘制模式且标注类型为POINT时处理
      if (!this.isDrawingActive || this.localAreaForm.geometryType !== 'POINT') {
        return;
      }
      
      try {
        // 确保latlng对象有效
        if (!latlng || typeof latlng.lat !== 'number' || typeof latlng.lng !== 'number') {
          console.warn('无效的地图点击坐标');
          return;
        }
        
        // 更新点标注坐标
        const coordinates = [latlng.lat, latlng.lng];
        this.localAreaForm.pointCoordinates = coordinates;
        
        // 使用事件通知绘制组件
        this.$emit('point-marked', {
          coordinates: coordinates,
          icon: this.localAreaForm.icon,
          name: this.localAreaForm.name
        });
        
        // 自动关闭绘制模式
        this.$nextTick(() => {
          // 如果是处于绘制模式，则完成绘制
          if (this.isDrawingActive) {
            this.$emit('toggle-drawing', {
              active: false,
              markType: 'point'
            });
            
            // 通知父组件可以保存点标注
            this.$emit('point-drawing-completed', {
              coordinates: coordinates,
              icon: this.localAreaForm.icon
            });
          }
        });
      } catch (error) {
        console.error('处理点标注点击事件出错:', error);
      }
    },

    // 测试点标注联动功能
    testPointAnnotationBinding() {
      console.log('=== 测试点标注联动功能 ===');
      
      // 测试几何类型切换
      console.log('1. 测试几何类型切换到点标注');
      this.localAreaForm.geometryType = 'POINT';
      
      // 验证区域类型是否自动设置为OTHER
      console.log('区域类型是否自动设置为OTHER:', this.localAreaForm.areaType === 'OTHER');
      
      // 测试图标选择
      console.log('2. 测试图标选择');
      this.selectPointIcon('el-icon-video-camera');
      console.log('当前选中图标:', this.localAreaForm.iconName);
      
      // 测试颜色变更
      console.log('3. 测试颜色变更');
      this.handleColorChange('#ff6600');
      console.log('当前颜色:', this.localAreaForm.color);
      
      // 测试名称变更
      console.log('4. 测试名称变更');
      this.handleNameInput('测试点标注');
      console.log('当前名称:', this.localAreaForm.name);
      
      console.log('=== 点标注联动功能测试完成 ===');
    },
    
    // 调试图标更新功能
    debugIconUpdate(iconName = 'el-icon-video-camera') {
      console.log('=== 调试图标更新功能 ===');
      console.log('当前表单状态:', {
        geometryType: this.localAreaForm.geometryType,
        icon: this.localAreaForm.icon,
        id: this.localAreaForm.id,
        pointCoordinates: this.localAreaForm.pointCoordinates,
        isEditingExisting: this.isEditingExisting
      });
      
      // 获取桥梁地图组件
      const bridgeMapComponent = this.findBridgeMapComponent();
      console.log('桥梁地图组件:', bridgeMapComponent ? '找到' : '未找到');
      
      if (bridgeMapComponent) {
        console.log('地图组件状态:', {
          pointMarkers: bridgeMapComponent.pointMarkers ? bridgeMapComponent.pointMarkers.length : 0,
          savedAreas: bridgeMapComponent.savedAreas ? bridgeMapComponent.savedAreas.length : 0,
          currentArea: bridgeMapComponent.$store ? bridgeMapComponent.$store.state.currentArea : null
        });
        
        // 尝试更新图标
        console.log('调用selectPointIcon，图标:', iconName);
        this.selectPointIcon(iconName);
        
        // 检查更新后的状态
        setTimeout(() => {
          console.log('更新后状态:', {
            localFormIcon: this.localAreaForm.icon,
            pointMarkersIcon: bridgeMapComponent.pointMarkers.length > 0 ? 
              bridgeMapComponent.pointMarkers[0].iconName : '无点标注',
            mapUpdateCounter: bridgeMapComponent.mapUpdateCounter
          });
        }, 100);
      }
      
      console.log('=== 调试完成 ===');
    },
    
    // 调试保存功能
    debugSaveFunction() {
      console.log('=== 调试保存功能 ===');
      
      // 检查当前表单状态
      console.log('保存前表单状态:', {
        geometryType: this.localAreaForm.geometryType,
        icon: this.localAreaForm.icon,
        name: this.localAreaForm.name,
        areaName: this.localAreaForm.areaName,
        color: this.localAreaForm.color,
        areaType: this.localAreaForm.areaType,
        id: this.localAreaForm.id,
        pointCoordinates: this.localAreaForm.pointCoordinates
      });
      
      // 检查是否有绘制坐标
      console.log('绘制坐标状态:', {
        drawingCoordinates: this.drawingCoordinates,
        hasDrawingCoordinates: this.hasDrawingCoordinates,
        isEditing: this.isEditing,
        isEditingExisting: this.isEditingExisting
      });
      
      // 模拟保存过程中的数据处理
      const saveData = { ...this.localAreaForm };
      
      // 确保图标字段正确设置（模拟BridgeAreaController中的逻辑）
      if (saveData.geometryType === 'POINT') {
        if (!saveData.icon) {
          saveData.icon = 'el-icon-location';
        }
        console.log('点标注保存数据处理:', {
          icon: saveData.icon,
          几何类型: saveData.geometryType
        });
      }
      
      console.log('最终将要保存的数据:', saveData);
      console.log('=== 保存调试完成 ===');
      
      return saveData;
    },
    
    // 调试颜色同步功能
    debugColorSync() {
      console.log('=== 调试颜色同步功能 ===');
      
      console.log('当前组件状态:', {
        'localAreaForm.color': this.localAreaForm.color,
        'localAreaForm.id': this.localAreaForm.id,
        'localAreaForm.geometryType': this.localAreaForm.geometryType,
        'isEditing': this.isEditing,
        'isEditingExisting': this.isEditingExisting,
        'isSaving': this.isSaving
      });
      
      // 查找对应的已保存区域，检查其颜色
      if (this.localAreaForm.id && this.areas) {
        const matchedArea = this.areas.find(area => area.id === this.localAreaForm.id);
        if (matchedArea) {
          console.log('匹配的已保存区域:', {
            id: matchedArea.id,
            name: matchedArea.name || matchedArea.areaName,
            color: matchedArea.color,
            geometryType: matchedArea.geometryType
          });
          
          // 检查颜色是否一致
          if (matchedArea.color !== this.localAreaForm.color) {
            console.warn('颜色不一致！已保存区域颜色:', matchedArea.color, '当前表单颜色:', this.localAreaForm.color);
          } else {
            console.log('✅ 颜色同步正确');
          }
        } else {
          console.warn('未找到匹配的已保存区域，ID:', this.localAreaForm.id);
        }
      }
      
      console.log('=== 颜色同步调试完成 ===');
    },
    
    // 调试名称同步功能
    debugNameSync() {
      console.log('=== 调试名称同步功能 ===');
      
      console.log('当前组件状态:', {
        'localAreaForm.name': this.localAreaForm.name,
        'localAreaForm.areaName': this.localAreaForm.areaName,
        'localAreaForm.areaType': this.localAreaForm.areaType,
        'localAreaForm.id': this.localAreaForm.id,
        'localAreaForm.geometryType': this.localAreaForm.geometryType,
        'hasUserEditedName': this.hasUserEditedName,
        'isEditing': this.isEditing,
        'isEditingExisting': this.isEditingExisting,
        'editMode': this.editMode,
        'isSaving': this.isSaving
      });
      
      // 检查默认名称逻辑
      const defaultName = this.areaTypeNameMap[this.localAreaForm.areaType];
      console.log('区域类型默认名称:', {
        areaType: this.localAreaForm.areaType,
        defaultName: defaultName,
        currentName: this.localAreaForm.name,
        isDefaultName: defaultName === this.localAreaForm.name
      });
      
      // 查找对应的已保存区域，检查其名称
      if (this.localAreaForm.id && this.areas) {
        const matchedArea = this.areas.find(area => area.id === this.localAreaForm.id);
        if (matchedArea) {
          console.log('匹配的已保存区域:', {
            id: matchedArea.id,
            name: matchedArea.name,
            areaName: matchedArea.areaName,
            areaType: matchedArea.areaType,
            displayName: matchedArea.name || matchedArea.areaName,
            geometryType: matchedArea.geometryType
          });
          
          // 检查名称是否一致
          const savedName = matchedArea.name || matchedArea.areaName;
          const currentName = this.localAreaForm.name || this.localAreaForm.areaName;
          
          if (savedName !== currentName) {
            console.warn('❌ 名称不一致！已保存区域名称:', savedName, '当前表单名称:', currentName);
          } else {
            console.log('✅ 名称同步正确');
          }
          
          // 检查名称编辑标志的合理性
          const areaDefaultName = this.areaTypeNameMap[matchedArea.areaType];
          const shouldBeEdited = areaDefaultName && savedName !== areaDefaultName;
          
          console.log('名称编辑标志检查:', {
            hasUserEditedName: this.hasUserEditedName,
            shouldBeEdited: shouldBeEdited,
            isConsistent: this.hasUserEditedName === shouldBeEdited,
            areaDefaultName: areaDefaultName,
            savedName: savedName
          });
          
        } else {
          console.warn('未找到匹配的已保存区域，ID:', this.localAreaForm.id);
        }
      } else if (!this.localAreaForm.id) {
        console.log('当前为新建区域模式');
      }
      
      console.log('=== 名称同步调试完成 ===');
    },
    
    // 调试标注类型禁用功能
    debugGeometryTypeDisabled() {
      console.log('=== 调试标注类型禁用功能 ===');
      
      console.log('当前组件状态:', {
        'isEditing': this.isEditing,
        'isEditingExisting': this.isEditingExisting,
        'isGeometryTypeDisabled': this.isGeometryTypeDisabled,
        'localAreaForm.id': this.localAreaForm.id,
        'localAreaForm.geometryType': this.localAreaForm.geometryType,
        'editMode': this.editMode
      });
      
      if (this.isGeometryTypeDisabled) {
        console.log('✅ 标注类型选择器已正确禁用（编辑现有区域）');
        console.log('当前标注类型:', this.localAreaForm.geometryType);
        console.log('区域ID:', this.localAreaForm.id);
      } else {
        console.log('✅ 标注类型选择器已启用（新建区域）');
        console.log('当前标注类型:', this.localAreaForm.geometryType);
      }
      
      console.log('=== 标注类型禁用调试完成 ===');
    },
    
    // 调试字段保留和坐标解析功能
    debugFieldsAndCoordinates() {
      console.log('=== 调试字段保留和坐标解析功能 ===');
      
      console.log('当前组件状态:', {
        'isEditing': this.isEditing,
        'isEditingExisting': this.isEditingExisting,
        'editMode': this.editMode,
        'hasUserEditedName': this.hasUserEditedName,
        'isSaving': this.isSaving
      });
      
      console.log('表单字段状态:', {
        'localAreaForm.id': this.localAreaForm.id,
        'localAreaForm.geometryType': this.localAreaForm.geometryType,
        'localAreaForm.name': this.localAreaForm.name,
        'localAreaForm.color': this.localAreaForm.color,
        'localAreaForm.icon': this.localAreaForm.icon,
        'localAreaForm.description': this.localAreaForm.description,
        'localAreaForm.pointCoordinates': this.localAreaForm.pointCoordinates,
        'areaForm.coordinates': this.areaForm.coordinates
      });
      
      // 【新增】专门诊断坐标解析问题
      if (this.localAreaForm.geometryType === 'POINT') {
        console.log('🔍 点标注坐标诊断:');
        
        // 检查原始坐标数据
        if (this.areaForm.coordinates) {
          console.log('📝 原始坐标数据:', {
            coordinates: this.areaForm.coordinates,
            type: typeof this.areaForm.coordinates,
            length: this.areaForm.coordinates?.length
          });
          
          // 尝试手动解析测试
          try {
            const testCoords = JSON.parse(this.areaForm.coordinates);
            console.log('🧪 手动解析测试成功:', testCoords);
            console.log('🧪 解析结果分析:', {
              isArray: Array.isArray(testCoords),
              length: testCoords?.length,
              firstElement: testCoords[0],
              firstElementIsArray: Array.isArray(testCoords[0]),
              shouldMatchFormat: testCoords.length === 2 && !Array.isArray(testCoords[0]) ? '[lat, lng]' : '[[lat, lng]]'
            });
          } catch (error) {
            console.error('🧪 手动解析测试失败:', error);
          }
        } else {
          console.warn('⚠️ 缺少原始坐标数据');
        }
        
        // 检查点坐标显示条件（修复后的逻辑）
        const shouldShowCoordinatesSection = this.localAreaForm.geometryType === 'POINT';
        const hasValidCoordinates = !!this.localAreaForm.pointCoordinates;
        
        console.log('📍 坐标显示条件检查:', {
          geometryType: this.localAreaForm.geometryType,
          shouldShowCoordinatesSection: shouldShowCoordinatesSection,
          hasValidCoordinates: hasValidCoordinates,
          pointCoordinates: this.localAreaForm.pointCoordinates,
          coordinateDisplay: hasValidCoordinates ? '显示具体坐标' : '显示"未设置"'
        });
        
        if (shouldShowCoordinatesSection) {
          console.log('✅ 点标注坐标区域会显示');
          if (hasValidCoordinates) {
            console.log('✅ 会显示具体的经纬度坐标');
            console.log('📍 具体坐标值:', {
              纬度: this.localAreaForm.pointCoordinates[0],
              经度: this.localAreaForm.pointCoordinates[1],
              纬度DMS: this.formatLatitudeDMS(this.localAreaForm.pointCoordinates[0]),
              经度DMS: this.formatLongitudeDMS(this.localAreaForm.pointCoordinates[1])
            });
          } else {
            console.log('ℹ️ 会显示"未设置"提示');
            console.warn('❗ 坐标解析可能失败，请检查上面的解析日志');
          }
        } else {
          console.warn('❌ 点标注坐标区域不会显示');
        }
      } else {
        console.log('当前不是点标注，坐标区域不显示');
      }
      
      // 检查字段保留逻辑
      if (this.editMode === 'new' && !this.localAreaForm.id) {
        console.log('新建模式字段保留检查:', {
          hasUserEditedName: this.hasUserEditedName,
          isNameCustom: this.localAreaForm.name && this.localAreaForm.name !== this.areaTypeNameMap[this.localAreaForm.areaType],
          isColorCustom: this.localAreaForm.color && this.localAreaForm.color !== getAreaColor(this.localAreaForm.areaType),
          isIconCustom: this.localAreaForm.icon && this.localAreaForm.icon !== 'el-icon-location'
        });
        
        console.log('预期行为：点击地图创建点标注时，上述字段应该被保留');
      }
      
      console.log('=== 字段保留和坐标解析调试完成 ===');
    }
  },
  watch: {
    // 监听外部传入的表单数据
    areaForm: {
      handler(newVal) {
        // 【保护机制】如果正在保存，跳过数据重置避免覆盖用户修改
        if (this.isSaving) {
          console.log('正在保存，跳过areaForm数据重置');
          return;
        }
        
        // 深拷贝避免直接引用和修改props
        const formData = JSON.parse(JSON.stringify(newVal || {}));
        
        // 确保有标注类型字段，默认为多边形
        if (!formData.geometryType) {
          formData.geometryType = 'POLYGON';
        }

        // 确保有颜色字段，默认为蓝色
        if (!formData.color) {
          // 只有在真正需要默认颜色时才设置（即新建区域或颜色字段确实为空）
          formData.color = getAreaColor(formData.areaType);
          console.log('设置默认区域颜色:', formData.color, '区域类型:', formData.areaType);
        } else {
          console.log('使用已有区域颜色:', formData.color, '区域ID:', formData.id);
        }
        
        // 【关键修复】智能合并数据，保留用户的修改
        if (this.localAreaForm && this.localAreaForm.id === formData.id && formData.id) {
          // 如果是同一个区域的更新，保留用户可能修改的字段
          console.log('智能合并区域数据，保留用户修改');
          
          // 保留用户可能修改的字段（图标和坐标），但颜色和名称优先使用外部数据
          const userModifiedFields = {
            icon: this.localAreaForm.icon
          };
          
          // 【修复坐标丢失问题】：保留现有的点坐标
          if (this.localAreaForm.pointCoordinates) {
            userModifiedFields.pointCoordinates = this.localAreaForm.pointCoordinates;
            console.log('保留现有点坐标:', this.localAreaForm.pointCoordinates);
          }
          
          // 合并数据：外部数据为基础，用户修改为优先
          this.localAreaForm = {
            ...formData,
            ...userModifiedFields
          };
          
          // 【修复颜色显示问题】：对于颜色，优先使用外部数据（即数据库保存的颜色）
          if (formData.color) {
            this.localAreaForm.color = formData.color;
            console.log('使用外部数据中的颜色:', formData.color);
          }
          
          // 【修复名称显示问题】：对于名称，优先使用外部数据（即数据库保存的名称）
          if (formData.name) {
            this.localAreaForm.name = formData.name;
            console.log('使用外部数据中的名称:', formData.name);
          }
          if (formData.areaName) {
            this.localAreaForm.areaName = formData.areaName;
            console.log('使用外部数据中的区域名称:', formData.areaName);
          }
          
          console.log('数据合并完成，保留用户选择的图标:', this.localAreaForm.icon, '使用实际颜色:', this.localAreaForm.color, '使用实际名称:', this.localAreaForm.name);
          
          // 【修复名称编辑标志】：编辑现有区域时，不重置名称编辑标志，保持当前状态
          console.log('编辑现有区域，保持名称编辑标志状态:', this.hasUserEditedName);
        } else {
          // 新区域或不同区域的处理
          if (!formData.id) {
            // 【修复字段重置问题】新建区域时，保留用户已编辑的字段
            console.log('新建区域，保留用户编辑的字段');
            
            // 保留用户可能编辑的字段
            const preservedFields = {};
            if (this.localAreaForm && this.hasUserEditedName && this.localAreaForm.name) {
              preservedFields.name = this.localAreaForm.name;
              preservedFields.areaName = this.localAreaForm.name;
              console.log('保留用户编辑的名称:', this.localAreaForm.name);
            }
            if (this.localAreaForm && this.localAreaForm.color && this.localAreaForm.color !== getAreaColor(formData.areaType)) {
              preservedFields.color = this.localAreaForm.color;
              console.log('保留用户编辑的颜色:', this.localAreaForm.color);
            }
            if (this.localAreaForm && this.localAreaForm.icon && this.localAreaForm.icon !== 'el-icon-location') {
              preservedFields.icon = this.localAreaForm.icon;
              console.log('保留用户编辑的图标:', this.localAreaForm.icon);
            }
            if (this.localAreaForm && this.localAreaForm.description) {
              preservedFields.description = this.localAreaForm.description;
              console.log('保留用户编辑的描述:', this.localAreaForm.description);
            }
            if (this.localAreaForm && this.localAreaForm.pointCoordinates) {
              preservedFields.pointCoordinates = this.localAreaForm.pointCoordinates;
              console.log('保留点标注坐标:', this.localAreaForm.pointCoordinates);
            }
            
            // 合并外部数据和保留的用户编辑字段
            this.localAreaForm = {
              ...formData,
              ...preservedFields
            };
            
            // 重置名称编辑标志
            this.hasUserEditedName = false;
            console.log('新建区域，重置名称编辑标志');
          } else {
            // 加载不同的现有区域，直接使用外部数据
        this.localAreaForm = formData;
            console.log('加载现有区域数据:', formData.id, '颜色:', formData.color, '名称:', formData.name);
            
            // 智能判断是否被用户编辑过
            const defaultName = this.areaTypeNameMap[formData.areaType];
            const currentName = formData.name || formData.areaName;
            
            if (defaultName && currentName === defaultName) {
              // 名称与默认名称相同，可能没有被编辑过
              this.hasUserEditedName = false;
              console.log('现有区域名称与默认名称相同，设置为未编辑状态');
            } else {
              // 名称与默认名称不同，可能被用户编辑过
              this.hasUserEditedName = true;
              console.log('现有区域名称与默认名称不同，设置为已编辑状态');
            }
          }
        }
        
        // 【修复点坐标显示问题】：如果是点标注，解析并设置点坐标
        if (formData.geometryType === 'POINT') {
          // console.log('🔍 开始解析点标注坐标，原始数据:', {
          //   geometryType: formData.geometryType,
          //   coordinates: formData.coordinates,
          //   coordinatesType: typeof formData.coordinates,
          //   pointCoordinates: formData.pointCoordinates
          // });
          
          if (formData.coordinates && typeof formData.coordinates === 'string') {
            try {
              console.log('📝 尝试解析坐标字符串:', formData.coordinates);
              // 解析坐标字符串
              const coords = JSON.parse(formData.coordinates);
              console.log('✅ JSON解析成功:', coords, '类型:', Array.isArray(coords), '长度:', coords?.length);
              
              if (Array.isArray(coords) && coords.length >= 1) {
                let pointCoords;
                if (Array.isArray(coords[0]) && coords[0].length === 2) {
                  // 格式：[[lat, lng]]
                  pointCoords = coords[0];
                  console.log('📍 使用二维数组格式:', pointCoords);
                } else if (coords.length === 2 && !Array.isArray(coords[0])) {
                  // 格式：[lat, lng]
                  pointCoords = coords;
                  console.log('📍 使用一维数组格式:', pointCoords);
                } else {
                  console.warn('❌ 点标注坐标格式不正确:', coords, '预期格式: [lat, lng] 或 [[lat, lng]]');
                }
                
                                 if (pointCoords && pointCoords.length === 2) {
                   // 【关键修复】：强制设置点坐标，确保不被后续逻辑覆盖
                   this.localAreaForm.pointCoordinates = pointCoords;
                   console.log('🎯 解析点标注坐标成功，已设置到localAreaForm.pointCoordinates:', this.localAreaForm.pointCoordinates);
                   
                   // 【强制保证】：使用 Vue.set 确保响应式更新，防止被覆盖
                   this.$set(this.localAreaForm, 'pointCoordinates', pointCoords);
                   
                   // 立即验证设置结果
                   this.$nextTick(() => {
                    //  console.log('🔍 验证设置结果:', {
                    //    设置后的值: this.localAreaForm.pointCoordinates,
                    //    是否为数组: Array.isArray(this.localAreaForm.pointCoordinates),
                    //    数组长度: this.localAreaForm.pointCoordinates?.length,
                    //    经度: this.localAreaForm.pointCoordinates?.[1],
                    //    纬度: this.localAreaForm.pointCoordinates?.[0]
                    //  });
                     
                     // 再次验证坐标是否被意外清空
                     if (!this.localAreaForm.pointCoordinates) {
                      //  console.error('🚨 坐标在设置后被清空，重新强制设置');
                       this.$set(this.localAreaForm, 'pointCoordinates', pointCoords);
                     }
                   });
                 } else {
                   console.error('❌ 解析后的坐标无效:', pointCoords);
                 }
              } else {
                console.warn('❌ 解析后不是有效的坐标数组:', coords);
              }
            } catch (error) {
              console.error('❌ 解析点标注坐标JSON失败:', error, '原始数据:', formData.coordinates);
            }
          } else if (formData.pointCoordinates) {
            // 如果直接有点坐标字段
            this.localAreaForm.pointCoordinates = formData.pointCoordinates;
            console.log('🎯 使用现有点坐标:', formData.pointCoordinates);
          } else {
            console.warn('⚠️ 点标注缺少坐标数据:', {
              hasCoordinates: !!formData.coordinates,
              coordinatesType: typeof formData.coordinates,
              hasPointCoordinates: !!formData.pointCoordinates
            });
          }
          
          // 同步更新点标注Label
          if (formData.name) {
          this.$nextTick(() => {
            this.updatePointMarkerLabel(formData.name);
          });
          }
        } else {
          console.log('ℹ️ 非点标注类型，跳过坐标解析:', formData.geometryType);
        }
        
        // 判断是否为新区域或首次编辑，设置模式和按钮状态
        if (!newVal.id) {
          this.editMode = 'new'; // 设置为新建模式
          this.isEditButtonDisabled = false; // 新建模式下编辑按钮可用
          
          // 如果有区域类型但没有名称，自动设置名称为区域类型
          if (newVal.areaType && this.areaTypeNameMap[newVal.areaType] && !newVal.name) {
            this.localAreaForm.name = this.areaTypeNameMap[newVal.areaType];
          }
        } else {
          this.editMode = 'edit'; // 设置为编辑模式
          this.isEditButtonDisabled = false; // 编辑模式下编辑按钮可用
        }
        
        // 当区域ID变化时，加载该区域的警戒规则
        if (newVal.id && this.isEditing) {
          this.loadAreaAlertRules(newVal.id);
        }
      },
      immediate: true,
      deep: true
    },

    //监听当前区域颜色变化
    'localAreaForm.color': function(newVal, oldVal) {
      // 避免重复触发，只有颜色真正变化时才处理
      if (newVal !== oldVal && newVal) {
        console.log('检测到颜色变化:', oldVal, '->', newVal, '区域ID:', this.localAreaForm.id, '几何类型:', this.localAreaForm.geometryType);
        this.$emit('change-area-color', newVal);
        
        // 如果是已保存的点标注，触发专门的点标注颜色变更事件
        if (this.localAreaForm.geometryType === 'POINT' && this.isEditingExisting && this.localAreaForm.id) {
          console.log('触发点标注颜色变更事件:', {
            color: newVal,
            areaId: this.localAreaForm.id,
            areaType: this.localAreaForm.areaType
          });
          this.$emit('point-color-changed', {
            color: newVal,
            areaId: this.localAreaForm.id,
            areaType: this.localAreaForm.areaType
          });
        }
      }
    },
    
    // 监听区域类型变化，自动设置区域名称和颜色
    'localAreaForm.areaType': function(newVal) {
      // if(this.localAreaForm.areaType == newVal){
      //   console.log('localAreaForm.areaType监听区域类型变化，区域类型未变化，不进行处理',newVal);
      //   return;
      // }
      // 确保区域类型变更被传递给父组件
      this.$emit('change-area-type', newVal);
      if(newVal == 'POINT'){
        console.log('监测到区域类型(areaType)错误设置为POINT，自动纠正为OTHER（POINT是几何类型而非区域类型）');
        newVal = 'OTHER';
      }
      
      // 当选择区域类型时，自动设置区域名称为对应的显示名称
      if (newVal && this.areaTypeNameMap[newVal]) {
        const displayName = this.areaTypeNameMap[newVal];
        
        // 【修复名称自动设置】：仅在新建区域时自动设置名称，编辑现有区域时保持原有名称
        if (!this.isEditingExisting || !this.localAreaForm.id) {
          // 只有新建区域时才自动设置名称，编辑现有区域时保持原有名称
          this.localAreaForm.name = displayName;
          console.log('新建区域，设置默认名称:', displayName);
          // 同步更新点标注Label（如果是点标注）
          if (this.localAreaForm.geometryType === 'POINT') {
            this.updatePointMarkerLabel(displayName);
          }
        } else {
          console.log('编辑现有区域，保持原有名称:', this.localAreaForm.name, '区域类型:', newVal);
        }

        // 当选择区域类型时，自动设置区域颜色（仅在新建区域时）
        if (!this.isEditingExisting || !this.localAreaForm.id) {
          // 只有新建区域时才自动设置颜色，编辑现有区域时保持原有颜色
        this.localAreaForm.color = getAreaColor(newVal);
        this.handleColorChange(this.localAreaForm.color);
          console.log('新建区域，设置默认颜色:', this.localAreaForm.color);
        } else {
          console.log('编辑现有区域，保持原有颜色:', this.localAreaForm.color, '区域类型:', newVal);
        }

        // this.localAreaForm.areaType = newVal;
      }
    },
    
    // 监听标注几何类型变化
    'localAreaForm.geometryType': function(newVal, oldVal) {
      if (newVal !== oldVal) {
        // 通知父组件标注类型变化
        this.$emit('geometry-type-change', newVal);
        
        // 如果切换到点标注，自动设置区域类型为"OTHERS"（其他）
        if (newVal === 'POINT') {
          // 自动设置区域类型为OTHERS（仅在新建区域或区域类型确实不匹配时）
          if (this.localAreaForm.areaType !== 'OTHER') {
            // 如果是编辑现有区域且已经有合适的区域类型，则不强制修改
            if (!this.isEditingExisting || !this.localAreaForm.id) {
              this.localAreaForm.areaType = 'OTHER';
              console.log('新建点标注，自动设置区域类型为: OTHER');
              
              // 通知父组件区域类型变化
              this.$emit('change-area-type', 'OTHER');
            } else {
              console.log('编辑现有点标注，保持原区域类型:', this.localAreaForm.areaType);
            }
          }
          
          // 重置点坐标
          this.localAreaForm.pointCoordinates = null;
          
          // 如果已经处于绘制模式，需要重新触发绘制模式以更新图标信息
          if (this.isDrawingActive) {
            // 延迟执行以确保UI更新
            this.$nextTick(() => {
              // 传递新的绘制参数，保持绘制模式激活
              this.$emit('toggle-drawing', {
                active: true,
                markType: this.getMarkTypeFromGeometryType(newVal),
                iconName: this.localAreaForm.icon
              });
            });
          }
        } else if (this.isDrawingActive && oldVal === 'POINT') {
          // 如果从点标注切换到其他类型，也需要更新绘制模式
          this.$nextTick(() => {
            this.$emit('toggle-drawing', {
              active: true,
              markType: this.getMarkTypeFromGeometryType(newVal)
            });
          });
        }
      }
    },
    
    // 监听选中的区域变化
    selectedArea: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.selectedAreaId = newVal.id;
          
          // 更新localStorage中的区域选择
          localStorage.setItem('BridgeManagement-AreaSelection', JSON.stringify(newVal));
          
          // 如果当前在编辑模式，加载该区域的警戒规则
          if (this.isEditing && this.isEditingExisting) {
            this.loadAreaAlertRules(newVal.id);
          }
        } else if (newVal === null) {
          // 【关键修复】当selectedArea为null时，清除本地选中状态
          console.log('AreaPanel: selectedArea变为null，清除本地选中状态');
          this.selectedAreaId = null;
          
          // 清除localStorage中的区域选择
          localStorage.removeItem('BridgeManagement-AreaSelection');
          
          // 清除警戒规则列表
          this.areaAlertRules = [];
        }
      },
      immediate: true
    },

    // 监听选中区域ID变化
    selectedAreaId: {
      handler(newVal) {
        // 如果ID变更，但没有对应的区域，则清除本地存储
        if (!newVal) {
          localStorage.removeItem('BridgeManagement-AreaSelection');
        }
      }
    },
    
    // 编辑状态变化
    isEditing: {
      handler(newVal) {
        // 如果进入编辑模式，且有选中区域ID，加载警戒规则
        if (newVal && this.selectedAreaId && this.isEditingExisting) {
          this.loadAreaAlertRules(this.selectedAreaId);
        }
        
        // 如果退出编辑模式，清空警戒规则列表
        if (!newVal) {
          this.areaAlertRules = [];
        }
      },
      immediate: true
    },

    // 监听桥梁ID变化
    bridgeId: {
      handler(newVal) {
        // 如果有选中区域且有新的桥梁ID，尝试加载警戒规则
        if (newVal && this.selectedAreaId && this.isEditingExisting) {
          this.loadAreaAlertRules(this.selectedAreaId);
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.area-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 340px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  z-index: 10;
  max-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.area-panel-collapsed {
  width: auto;
  background-color: rgba(255, 255, 255, 0.9);
}

.panel-control {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-bottom: 1px solid #ebeef5;
  background-color: #f5f7fa;
  border-radius: 4px 4px 0 0;
  flex-shrink: 0;
}

.panel-title {
  margin: 0;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.collapse-icon {
  cursor: pointer;
  font-size: 16px;
  color: #606266;
  padding: 5px;
}

.collapse-icon:hover {
  color: #409EFF;
}

.panel-content {
  padding: 12px;
  overflow-y: auto;
  flex-grow: 1;
}

.panel-loading {
  padding: 15px 0;
}

.area-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.is-active {
  background-color: #409EFF;
  color: white;
}

.area-list-container {
  margin-bottom: 15px;
}

.list-title {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #303133;
  display: flex;
  align-items: center;
}

.area-count {
  font-size: 12px;
  color: #909399;
  margin-left: 5px;
}

.area-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.area-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  transition: background-color 0.2s;
}

.area-item:last-child {
  border-bottom: none;
}

.area-item:hover {
  background-color: #f5f7fa;
}

.area-item-selected {
  background-color: #ecf5ff;
}

.area-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
}

.area-name {
  font-size: 13px;
  color: #f0f2f5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.area-id {
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.area-actions {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

/* 区域隐藏时按钮灰显 */
.is-area-hidden {
  filter: grayscale(100%);
  opacity: 0.5;
}

.no-areas {
  padding: 15px;
  text-align: center;
  color: #909399;
  font-size: 13px;
}

.area-form {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 15px;
  border: 1px solid #ebeef5;
}

.form-item {
  margin-bottom: 12px;
}

.label {
  font-size: 13px;
  color: #606266;
  display: block;
  margin-bottom: 5px;
}

/* 表单标题栏样式 */
.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  margin-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.form-title {
  margin: 0;
  font-size: 14px;
  color: #303133;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-collapse-icon {
  cursor: pointer;
  font-size: 14px;
  color: #606266;
  padding: 4px;
  border-radius: 2px;
  transition: all 0.3s;
}

.form-collapse-icon:hover {
  color: #409EFF;
  background-color: #ecf5ff;
}

.form-content {
  transition: all 0.3s ease;
}

/* 警戒规则相关样式 */
.area-alert-rules {
  margin-top: 20px;
  border-top: 1px dashed #dcdfe6;
  padding-top: 15px;
}

.alert-rules-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.alert-rules-header h5 {
  margin: 0;
  font-size: 14px;
  color: #303133;
}

.alert-rules-list {
  max-height: 200px;
  overflow-y: auto;
}

.rules-loading {
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.alert-rule-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
}

.alert-rule-item:hover {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
}

.rule-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  overflow: hidden;
}

.rule-type {
  font-size: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 5px;
}

.rule-name {
  font-size: 13px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rule-actions {
  display: flex;
  gap: 5px;
}

.no-rules {
  padding: 15px;
  text-align: center;
  color: #909399;
  font-size: 13px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

/* 规则抽屉样式 */
.rule-drawer {
  overflow: hidden;
}

.rule-drawer-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.rule-form {
  flex: 1;
  overflow-y: auto;
}

.drawer-footer {
  padding: 10px 20px;
  border-top: 1px solid #e6e6e6;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin: 0 -20px;
  background-color: #fff;
}

.form-help {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.variable-tag {
  margin-right: 5px;
  margin-bottom: 5px;
  cursor: pointer;
}

.variable-tag:hover {
  opacity: 0.8;
}

/* 点标注图标选择器样式 */
.icon-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 5px;
}

.icon-item {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fff;
}

.icon-item:hover {
  border-color: #409EFF;
  background-color: #ecf5ff;
}

.icon-item i {
  font-size: 18px;
  color: #606266;
}

.icon-item.icon-selected {
  border-color: #409EFF;
  background-color: #ecf5ff;
}

.icon-item.icon-selected i {
  color: #409EFF;
}

/* 点标注经纬度样式 */
.point-coordinates {
  background-color: #f5f7fa;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  padding: 10px;
  margin-top: 5px;
}

.coordinate-item {
  display: flex;
  margin-bottom: 5px;
}

.coordinate-item:last-child {
  margin-bottom: 0;
}

.coordinate-label {
  font-weight: bold;
  color: #606266;
  width: 50px;
}

.coordinate-value {
  color: #303133;
  font-family: monospace;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .area-panel {
    width: 300px;
    left: 0;
    top: 0;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
  
  .area-panel-collapsed {
    transform: translateX(-290px);
  }
  
  .area-list {
    flex: 1;
  }
}

/* 修改全局抽屉样式 */
.el-drawer__container {
  position: absolute !important;
}

:deep(.el-drawer__mask) {
  background-color: transparent !important;
}
</style> 